import ProLayout, { PageContainer, BasicLayoutProps, MenuDataItem } from '@ant-design/pro-layout';
import { Route } from '@ant-design/pro-layout/lib/typings';
import {
  Breadcrumb,
  Row,
  Typography,
  Tooltip,
  Menu,
} from 'antd';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, {
  FC,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import './index.scss';

const HomeOutlined = () => <svg viewBox="64 64 896 896" focusable="false" data-icon="home" width="1em" height="1em" fill="currentColor" aria-hidden="true">
  <path d="M946.5 505L560.1 118.8l-25.9-25.9a31.5 31.5 0 00-44.4 0L77.5 505a63.9 63.9 0 00-18.8 46c.4 35.2 29.7 63.3 64.9 63.3h42.5V940h691.8V614.3h43.4c17.1 0 33.2-6.7 45.3-18.8a63.6 63.6 0 0018.7-45.3c0-17-6.7-33.1-18.8-45.2zM568 868H456V664h112v204zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7 23.1 23.1L882 542.3h-96.1z"></path>
</svg>;

const QuestionCircleOutlined = () => <svg viewBox="64 64 896 896" focusable="false" data-icon="question-circle" width="1em" height="1em" fill="currentColor" aria-hidden="true">
  <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
  <path d="M623.6 316.7C593.6 290.4 554 276 512 276s-81.6 14.5-111.6 40.7C369.2 344 352 380.7 352 420v7.6c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V420c0-44.1 43.1-80 96-80s96 35.9 96 80c0 31.1-22 59.6-56.1 72.7-21.2 8.1-39.2 22.3-52.1 40.9-13.1 19-19.9 41.8-19.9 64.9V620c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8v-22.7a48.3 48.3 0 0130.9-44.8c59-22.7 97.1-74.7 97.1-132.5.1-39.3-17.1-76-48.3-103.3zM472 732a40 40 0 1080 0 40 40 0 10-80 0z"></path>
</svg>;

const { Link: TextLink } = Typography;

export interface TdsBasicLayoutProps extends BasicLayoutProps {
  leftMenuRoutes: Route;
  overlayMenuRoutes?: Route;
  appSelector?: ReactNode;
  topMenu?: ReactNode;
  sailingPlan?: ReactNode;
  liteModeRoutes?: string[];
  defaultOpenKeys?: string[];
  collapsed?: boolean;
  setCollapsed?: (isCollaPsed: boolean) => void;
  handlePath?: (path: string) => void;
  mainMenuLink?: string;
  renderBanner?: (
    showBanner: boolean,
    setShowBanner: React.Dispatch<React.SetStateAction<boolean>>,
    showOverdueDataBanner: boolean,
    setShowOverdueDataBanner: React.Dispatch<React.SetStateAction<boolean>>,
    showArrearsBanner: boolean,
    setShowArrearsBanner: React.Dispatch<React.SetStateAction<boolean>>,
    showComplianceBanner: boolean,
    setShowComplianceBanner: React.Dispatch<React.SetStateAction<boolean>>,
  ) => ReactNode;
  renderArrearsBanner?: () => ReactNode;
  liteMode?: boolean;
  showBreadcrumb?: boolean;
  narrow?: boolean;
  menuProps?: BasicLayoutProps['menuProps'];
}

const BG_PATH_NAME = ['/setting/tag', '/setting/alert'];

export const BasicLayout: FC<TdsBasicLayoutProps> = (props) => {
  const {
    children,
    leftMenuRoutes,
    overlayMenuRoutes,
    logo,
    appSelector,
    topMenu,
    sailingPlan,
    liteMode = false,
    narrow = false,
    liteModeRoutes,
    title,
    className,
    collapsed,
    setCollapsed,
    handlePath,
    footerRender,
    mainMenuLink,
    renderBanner,
    renderArrearsBanner,
    showBreadcrumb = true,
    menuProps,
    ...restProps
  } = props;
  const [showBanner, setShowBanner] = useState(false);
  const [showOverdueDataBanner, setShowOverdueDataBanner] = useState(false);
  const [showArrearsBanner, setShowArrearsBanner] = useState(false);
  const [showComplianceBanner, setShowComplianceBanner] = useState(false);

  const flatten = (routes?: Route[], parent?: string) => {
    let flattenRoutes: Route[] = [];
    routes?.forEach((route) => {
      flattenRoutes.push({
        ...route,
        parent,
      });

      if (route.routes) {
        const subRoutes = flatten(route.routes, route.path);
        flattenRoutes = flattenRoutes.concat(subRoutes.map(subRoute => ({
          ...subRoute,
        })));
      }
    });
    return flattenRoutes;
  };

  // 打平后的路由配置，方便查找
  const flattenRoutes = React.useMemo(
    () => flatten([
      ...(leftMenuRoutes.routes || []),
      ...(overlayMenuRoutes?.routes || []),
    ]),
    [leftMenuRoutes, overlayMenuRoutes],
  );

  /**
   * 获取最后一次点击的菜单的openKeys，效果是只展开一个一级菜单，其他关闭
   * @param lastKey
   * @returns
   */
  const getLastOpenKeys = (lastKey: string) => {
    if (!lastKey) return [];
    let tempKey = lastKey;
    const lastOpenKeys: string[] = [lastKey];
    while (true) {
      // eslint-disable-next-line no-loop-func
      const currentRoute: any = flattenRoutes.find(route => route.path === tempKey);
      if (currentRoute?.parent) {
        tempKey = currentRoute.parent;
        lastOpenKeys.push(currentRoute.parent);
      } else {
        break;
      }
    }

    return lastOpenKeys;
  };

  const location = useLocation();

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    const initRoute = flattenRoutes.find(route => route.path === location.pathname);
    const openKeys = getLastOpenKeys(initRoute?.parent);
    const selectedKeys = initRoute?.path ? [initRoute.path] : [];
    setOpenKeys(openKeys);
    setSelectedKeys(selectedKeys);
  }, [flattenRoutes]);

  const isLiteMode = () => liteMode || liteModeRoutes?.includes(location.pathname);

  const contentClassName = classnames({
    'page-container': true,
    'bugly-page-container': true,
    'bugly-page-container-lite': isLiteMode(),
    // 窄版
    'bugly-page-narrow': narrow,
  });

  const breadcrumb = (): ReactNode => {
    const routes: Route[] = [];
    let path = location.pathname;
    do {
      // eslint-disable-next-line no-loop-func
      const route = flattenRoutes.find(item => item.path === path);
      if (route) {
        routes.push(route);
        path = route.parent;
      } else {
        path = '';
      }
    } while (!!path);

    return (
      <div>
        {renderArrearsBanner?.()}
        <Breadcrumb
          className={classnames({
            breadcrumb: true,
            'narrow-mode': narrow,
          })}
        >
          <Breadcrumb.Item key="/">
            <Link to={mainMenuLink || '/'}>
              <HomeOutlined />
            </Link>
          </Breadcrumb.Item>
          {routes?.reverse()?.map((route: MenuDataItem) => (
            <Breadcrumb.Item key={route.name}>
              {route.name}
              {route?.breadcrumbSuffix || ''}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>
    );
  };

  const headerContentRender = () => (
    <Row className="bugly-header-extra">
      <div>
        {isLiteMode() ? null : appSelector}
        {sailingPlan}
      </div>
      <div style={{
        textAlign: 'right',
        flex: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}>
        {topMenu}
      </div>
    </Row>
  );

  const isRouteMenuChange = (openKeys: string[]) => openKeys.some((openKey) => {
    const currentRoute: any = flattenRoutes.find(route => route.path === openKey);
    return !currentRoute?.parent;
  });

  const onOpenChange = (keys: string[]) => {
    if (isEmpty(keys) || !isRouteMenuChange(keys)) {
      return setOpenKeys(keys);
    }

    const lastKey = isEmpty(keys) ? '' : keys.pop();
    setOpenKeys(getLastOpenKeys(lastKey as string));
  };

  const onSelect = (e: { selectedKeys: string[] }) => {
    const { selectedKeys } = e;
    setSelectedKeys(selectedKeys);
  };

  const headerTitleRender = (logo: ReactNode) => <>{logo}</>;

  const menuItemRender = (item: MenuDataItem, dom: ReactNode) => {
    if (item.href) {
      return (
        <TextLink
          style={{
            paddingLeft: item.icon ? 0 : '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          href={item.href}
          target={item.target}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
            {dom}
            {item.suffix}
          </div>
          <span style={{ paddingRight: 18, display: 'flex',
            alignItems: 'center' }}>{item.extra}</span>
        </TextLink>
      );
    }
    if (handlePath) {
      return (
        <div
          className={classnames({
            ['ant-layout-menu-item']: true,
            ['ant-layout-menu-item-selected']: selectedKeys?.[0] === item?.path,
          })}
          key={item.path}
          onClick={() => {
            item.onMenuClick?.();
            handlePath(item.path as string);
          }}
          style={{
            textDecoration: 'none',
            paddingLeft: item.icon ? 0 : '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <div
            style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {dom}
            {item.suffix}
            {item?.tooltip ? (
              <Tooltip
                title={item.tooltip?.tips}
                placement={item.tooltip?.placement || 'top'}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <QuestionCircleOutlined />
                </span>
              </Tooltip>
            ) : null}
          </div>
          <span
            style={{ paddingRight: 18, display: 'flex', alignItems: 'center' }}
          >
            {item.extra}
          </span>
        </div>
      );
    }
    return (
      <Link
        key={item.path}
        style={{
          textDecoration: 'none',
          paddingLeft: item.icon ? 0 : '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
        to={item.path as string}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
          {dom}
          {item.suffix}
        </div>
        <span style={{ paddingRight: 18, display: 'flex',
          alignItems: 'center' }}>{item.extra}</span>
      </Link>
    );
  };

  const subMenuItemRender = (item: MenuDataItem, dom: ReactNode) => (
    <div style={{
      paddingLeft: item.flag === 'subMenu' ? '11px' : 0,
      display: 'flex',
      alignItems: 'center',
    }}>
      <div style={{ flex: 1 }}>
        {dom}
        {item.suffix}
      </div>
      {item.extra}
    </div>
  );

  const renderOverlaysMenuItems = (routes?: Route[]) => {
    const renderMenuItem = (menus?: Route[]) => menus?.map((menu) => {
      const { path, icon, name, routes, hideInMenu } = menu;
      if (hideInMenu) {
        return null;
      }
      const key = path as string;

      if (routes?.length) {
        return (
          <Menu.SubMenu
            key={key}
            title={subMenuItemRender(menu, name)}
            icon={icon}
          >
            {renderMenuItem(routes)}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={key}
          icon={icon}
        >
          {menuItemRender(menu, name)}
        </Menu.Item>
      );
    });

    return renderMenuItem(routes);
  };


  const overlaysMenuRender = () => (collapsed ? (
    overlayMenuRoutes?.routes?.map(route => (
      <div className={'overlay-collapsed-icon'}>
        {route.icon}
      </div>
    ))) : [<>
    <Menu
      mode="inline"
      inlineIndent={16}
      className={'overlay-menu'}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      selectedKeys={selectedKeys}
      onSelect={onSelect}
    >
      {renderOverlaysMenuItems(overlayMenuRoutes?.routes)}
    </Menu>
  </>]);

  return (
    <ProLayout
      menuProps={{
        openKeys,
        onOpenChange,
        selectedKeys,
        onSelect,
        ...(menuProps || {}),
      }}
      footerRender={footerRender}
      className={classnames(
        `${className} tds-layout`,
        showBanner || showOverdueDataBanner || showArrearsBanner || showComplianceBanner ? 'show-banner' : '',
        BG_PATH_NAME.includes(location.pathname) ? 'tag-bg' : '',
        overlayMenuRoutes?.routes ? 'show-overlay' : '',
      )}
      route={leftMenuRoutes}
      title={title}
      logo={logo}
      location={location}
      navTheme="light"
      layout="mix"
      headerTheme="light"
      fixSiderbar
      fixedHeader
      collapsed={collapsed}
      onCollapse={setCollapsed}
      menu={{ defaultOpenAll: true }}
      headerTitleRender={headerTitleRender as any}
      headerContentRender={headerContentRender}
      menuRender={isLiteMode() ? false : undefined}
      menuItemRender={menuItemRender}
      subMenuItemRender={subMenuItemRender}
      links={overlaysMenuRender()}
      {...restProps}
    >
      {renderBanner?.(
        showBanner,
        setShowBanner,
        showOverdueDataBanner,
        setShowOverdueDataBanner,
        showArrearsBanner,
        setShowArrearsBanner,
        showComplianceBanner,
        setShowComplianceBanner,
      )}
      <PageContainer
        className={contentClassName}
        header={{
          title: false,
          subTitle: false,
          breadcrumbRender: showBreadcrumb ? breadcrumb : undefined,
        }}
      >
        {children}
      </PageContainer>
    </ProLayout>
  );
};
